import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Sidebar from "@/components/Sidebar";
import { 
  Menu,
  Info,
  HelpCircle
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TCOAnalysis() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [tcoResults, setTcoResults] = useState([]);
  const [timeframe, setTimeframe] = useState("annual");
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bundleId = params.get("bundle");
    if (bundleId) setSelectedBundles([bundleId]);
    fetchBundles();
  }, [location.search]);

  useEffect(() => {
    if (selectedBundles.length > 0) {
      calculateTCO();
    }
  }, [selectedBundles, timeframe]);

  const fetchBundles = async () => {
    try {
      const response = await axios.get(`${API}/bundles`, { withCredentials: true });
      setBundles(response.data.bundles);
      
      // Auto-select first 3 bundles if none selected
      const params = new URLSearchParams(location.search);
      if (!params.get("bundle") && response.data.bundles.length > 0) {
        setSelectedBundles(response.data.bundles.slice(0, 3).map(b => b.bundle_id));
      }
    } catch (error) {
      console.error("Error fetching bundles:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTCO = async () => {
    if (selectedBundles.length === 0) return;
    
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/tco/calculate`, {
        bundle_ids: selectedBundles,
        years: timeframe === "annual" ? 1 : 3
      }, { withCredentials: true });
      setTcoResults(response.data.tco_results);
    } catch (error) {
      console.error("Error calculating TCO:", error);
    } finally {
      setCalculating(false);
    }
  };

  const toggleBundleSelection = (bundleId) => {
    setSelectedBundles(prev => 
      prev.includes(bundleId)
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId].slice(0, 3)
    );
  };

  // Get first result for detailed breakdown
  const primaryResult = tcoResults[0];

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)}
          currentPath={location.pathname}
        />

        <div className="lg:pl-64">
          {/* Header */}
          <header className="sticky top-0 z-30 glass border-b border-border">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 hover:bg-white/5 rounded-lg"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="font-heading text-xl font-semibold">Total Cost of Ownership (TCO) & Market Savings</h1>
                </div>
              </div>
            </div>
          </header>

          <main className="p-6 space-y-8">
            {/* TCO for Selected Solutions */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-lg">TCO for Selected Solutions</h2>
                
                {/* Timeframe Toggle */}
                <div className="tco-toggle flex">
                  <button
                    onClick={() => setTimeframe("annual")}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      timeframe === "annual" ? "tco-toggle-active" : "text-muted-foreground"
                    }`}
                    data-testid="toggle-annual"
                  >
                    Annual
                  </button>
                  <button
                    onClick={() => setTimeframe("3-year")}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      timeframe === "3-year" ? "tco-toggle-active" : "text-muted-foreground"
                    }`}
                    data-testid="toggle-3-year"
                  >
                    3-Year
                  </button>
                </div>
              </div>

              {/* Solution Selection */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-24 rounded-lg" />
                  ))
                ) : (
                  bundles.slice(0, 6).map((bundle, i) => {
                    const isSelected = selectedBundles.includes(bundle.bundle_id);
                    const tcoData = tcoResults.find(r => r.bundle_id === bundle.bundle_id);
                    
                    return (
                      <button
                        key={bundle.bundle_id}
                        onClick={() => toggleBundleSelection(bundle.bundle_id)}
                        className={`p-4 rounded-lg text-left transition-all ${
                          isSelected
                            ? i === 0 
                              ? "bg-[#00FF88]/10 border-2 border-[#00FF88]"
                              : "bg-secondary border-2 border-[#007AFF]/20"
                            : "bg-secondary/50 border border-border hover:border-[#007AFF]/20"
                        }`}
                        data-testid={`select-tco-${bundle.bundle_id}`}
                      >
                        <p className="text-xs text-muted-foreground mb-1">
                          Solution {String.fromCharCode(65 + i)} TCO
                        </p>
                        <p className="text-2xl font-bold font-heading">
                          ₹{tcoData ? tcoData.total_cost.toLocaleString() : bundle.base_price.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{bundle.name}</p>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Detailed TCO Breakdown */}
              {primaryResult && (
                <div className="space-y-6">
                  <h3 className="font-heading font-semibold">
                    Detailed TCO Breakdown ({timeframe === "annual" ? "Annual" : "3-Year"})
                  </h3>

                  <div className="overflow-x-auto">
                    <table className="w-full data-table">
                      <thead>
                        <tr>
                          <th className="p-4 text-left">Category</th>
                          <th className="p-4 text-right">Your Price</th>
                          <th className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              Market Prices
                              <Tooltip>
                                <TooltipTrigger>
                                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Average market prices from retail sources</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-4">Hardware</td>
                          <td className="p-4 text-right font-mono">₹{primaryResult.hardware_cost.toLocaleString()}</td>
                          <td className="p-4 text-right font-mono text-muted-foreground">₹{primaryResult.market_hardware_cost.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td className="p-4">Software Licenses</td>
                          <td className="p-4 text-right font-mono">₹{primaryResult.software_cost.toLocaleString()}</td>
                          <td className="p-4 text-right font-mono text-muted-foreground">₹{primaryResult.market_software_cost.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td className="p-4">Support & Maintenance</td>
                          <td className="p-4 text-right font-mono">₹{primaryResult.support_cost.toLocaleString()}</td>
                          <td className="p-4 text-right font-mono text-muted-foreground">₹{primaryResult.market_support_cost.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td className="p-4">Implementation Services</td>
                          <td className="p-4 text-right font-mono">₹{primaryResult.implementation_cost.toLocaleString()}</td>
                          <td className="p-4 text-right font-mono text-muted-foreground">₹{primaryResult.market_implementation_cost.toLocaleString()}</td>
                        </tr>
                        <tr className="font-semibold bg-secondary/30">
                          <td className="p-4">Total {timeframe === "annual" ? "Annual" : "3-Year"} Cost</td>
                          <td className="p-4 text-right font-mono">₹{primaryResult.total_cost.toLocaleString()}</td>
                          <td className="p-4 text-right font-mono text-muted-foreground">₹{primaryResult.market_total_cost.toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Market Savings Comparison */}
            {primaryResult && (
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="font-heading font-semibold text-lg mb-6">Market Savings Comparison</h2>

                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="p-4 text-left text-[#00FF88]">Your Annual Savings</th>
                      <th className="p-4 text-right text-[#007AFF]">vs. Market Average</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4">Direct Purchase Savings</td>
                      <td className="p-4 text-right font-mono text-[#00FF88]">
                        ₹{Math.round(primaryResult.savings * 0.65).toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-4">Bundle Discount Savings</td>
                      <td className="p-4 text-right font-mono text-[#00FF88]">
                        ₹{Math.round(primaryResult.savings * 0.35).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="font-semibold">
                      <td className="p-4 text-[#00FF88]">Total Annual Market Savings</td>
                      <td className="p-4 text-right font-mono text-[#00FF88]">
                        ₹{primaryResult.savings.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#007AFF] shrink-0 mt-0.5" />
                <p className="text-sm text-[#007AFF]">
                  The TCO viewer helps you make informed choices about services and support packages, 
                  while market savings highlight your value.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
